import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Save, Loader2 } from 'lucide-react';
import axiosApi from '../server/service'; // Your pre-configured axios instance
import toast from 'react-hot-toast';


import SearchableDropdown from '../components/customUI/SearchableDropdown'; 



// Define the structure for the form data
interface FabricFormState {
    fabricType: string;
    vendorDetails: string;
    panna: string;
    pannaRate: string; 
    agentName: string;
    discountDetails: string;
    masterRecordRef: string;
}

const CreateFabricTypePage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isEditMode = Boolean(id);

    const [formData, setFormData] = useState<FabricFormState>({
        fabricType: '',
        vendorDetails: '',
        panna: '',
        pannaRate: '',
        agentName: '',
        discountDetails: '',
        masterRecordRef: '',
    });

    const [vendorOptions, setVendorOptions] = useState<{ label: string; value: string }[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(isEditMode);
    const [isLoadingVendors, setIsLoadingVendors] = useState(true);


    useEffect(() => {
        if (isEditMode) {
            const fetchFabricData = async () => {
                try {

                    const { data } = await axiosApi.get(`/api/fabricTypes/get-fabrictype-by-id/${id}`);
                     const fabricData = data.data;
                    const fetchedData = {
                        fabricType: fabricData.fabricType || '',
                        vendorDetails: fabricData.vendorDetails?._id || '',
                        panna: fabricData.panna || '',
                        pannaRate: fabricData.pannaRate?.toString() || '',
                        agentName: fabricData.agentName || '',
                        discountDetails: fabricData.discountDetails || '',
                        masterRecordRef: fabricData.masterRecordRef || '',
                    };
                    setFormData(fetchedData);
                } catch (error) {
                    toast.error("Failed to fetch fabric data.");
                    navigate('/fabric-type-master'); // Redirect to your listing page
                } finally {
                    setIsPageLoading(false);
                }
            };
            fetchFabricData();
        }
    }, [id, isEditMode, navigate]);

    // --- FETCH VENDORS FOR DROPDOWN ---
    useEffect(() => {
        const fetchVendors = async () => {
            setIsLoadingVendors(true);
            try {
               
                const response = await axiosApi.get('/api/vendors/getVendorsForDropdown', {
                    params: { vendorType: 'Fabric Supplier' }
                });
                if (response.data.success && Array.isArray(response.data.data)) {
                    const formattedVendors = response.data.data.map((vendor: any) => ({
                        label: vendor.vendorName,
                        value: vendor._id,
                    }));
                    setVendorOptions(formattedVendors);
                }
            } catch (error) {
                toast.error("Failed to load vendors.");
            } finally {
                setIsLoadingVendors(false);
            }
        };
        fetchVendors();
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleVendorChange = (value: string) => {
        setFormData((prev) => ({ ...prev, vendorDetails: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        const payload = {
            ...formData,
            pannaRate: parseFloat(formData.pannaRate) || 0, // Convert to number before sending
        };

        try {
            if (isEditMode) {

                await axiosApi.put(`api/fabricTypes/update-fabrictype/${id}`, payload);
                toast.success("Fabric Type updated successfully!");
            } else {

                await axiosApi.post('/api/fabricTypes/create-fabricTypes', payload);
                toast.success("Fabric Type created successfully!");
            }
            navigate('/fabric-type-master'); 
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "An error occurred.");
        } finally {
            setIsLoading(false);
        }
    };

    if (isPageLoading) {
        return (
            <div className="flex items-center justify-center h-96">
                <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                        {isEditMode ? "Edit Fabric Type" : "Create Fabric Type"}
                    </h1>
                    <p className="text-gray-500 mt-1">Manage fabric master data entries.</p>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                    <Link to="/fabric-type-master" className="w-full sm:w-auto text-center px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100">
                        Cancel
                    </Link>
                    <button type="submit" disabled={isLoading} className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-green-500 text-white hover:bg-green-600 disabled:opacity-50">
                        <Save className="w-4 h-4" />
                        <span>{isLoading ? "Saving..." : "Save Changes"}</span>
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Fabric Type/Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Fabric Name/Type</label>
                        <input type="text" name="fabricType" value={formData.fabricType} onChange={handleChange} placeholder="e.g., Cotton Blend, Silk" className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
                    </div>

                    {/* Vendor Details */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Details</label>
                        <SearchableDropdown options={vendorOptions} value={formData.vendorDetails} onChange={handleVendorChange} placeholder="Select a vendor..." loading={isLoadingVendors} />
                    </div>

                    {/* Panna */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Panna</label>
                        <input type="text" name="panna" value={formData.panna} onChange={handleChange} placeholder="" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                    </div>

                    {/* Panna Rate */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Panna Rate</label>
                        <input type="number" step="0.01" name="pannaRate" value={formData.pannaRate} onChange={handleChange} placeholder="0.00" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                    </div>

                    {/* Agent Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Agent Name</label>
                        <input type="text" name="agentName" value={formData.agentName} onChange={handleChange} placeholder="Agent's name" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                    </div>

                    {/* Discount Details */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Discount Details</label>
                        <input type="text" name="discountDetails" value={formData.discountDetails} onChange={handleChange} placeholder="e.g., 10% off on bulk" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                    </div>

                    {/* Master Record Reference */}
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Master Record Reference</label>
                        <input type="text" name="masterRecordRef" value={formData.masterRecordRef} onChange={handleChange} placeholder="Internal reference code or ID" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                    </div>
                </div>
            </div>
        </form>
    );
};

export default CreateFabricTypePage;